import React, { useState, useEffect } from 'react';
import config from '../../config/config';
import * as XLSX from 'xlsx'; 
import { Sidebar } from '../moderator/Sidebar';
import { Navbar } from '../Navbar';

export function SupLedger() {
  const [ledgerData, setLedgerData] = useState([]);
  const [selectedSup, setSelectedSup] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);

  const today = new Date().toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const token = localStorage.getItem('token');

  // Suppliers load
  useEffect(() => {
    fetch(`${config.baseUrl}/api/supplierDetails/supplier`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
    .then(res => res.json())
    .then(data => setSuppliers(Array.isArray(data) ? data : []))
    .catch(err => console.error("Suppliers load error:", err));
  }, [token]);

  // Ledger load
  useEffect(() => {
    if (selectedSup) {
      setLoading(true);
      fetch(`${config.baseUrl}/api/supplierDetails/ledger?supId=${selectedSup}&startDate=${startDate}&endDate=${endDate}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      .then(res => res.json())
      .then(data => setLedgerData(Array.isArray(data) ? data : (data.data || [])))
      .catch(err => {
        console.error("Ledger error:", err);
        setLedgerData([]);
      })
      .finally(() => setLoading(false));
    }
  }, [selectedSup, startDate, endDate, token]);

  // --- Print Handler Fix ---
  const handlePrintClick = () => {
    if (!selectedSup) return alert("Pehle supplier select karein!");
    
    // List se selected supplier ka naam nikalna
    const supObj = suppliers.find(s => String(s.sup_id) === String(selectedSup));
    const supName = supObj ? supObj.sup_name : "Supplier";

    // URL parameters ke saath navigate karna (supName lazmi bhejein)
    const printUrl = `/supplier/print-ledger?supId=${selectedSup}&startDate=${startDate}&endDate=${endDate}&supName=${encodeURIComponent(supName)}`;
    window.open(printUrl, '_blank');
  };

  // --- Export to Excel ---
  const exportToExcel = () => {
    if (ledgerData.length === 0) return alert("Pehle data load karein!");
    const excelData = ledgerData.map(row => ({
      Date: new Date(row.created_at).toLocaleDateString('en-GB'),
      Type: row.pay_type,
      "Total Bill": row.total_amount,
      Paid: row.pay_type?.toLowerCase().includes('adv') ? 0 : row.pay_amount,
      "Percentage (%)": row.pay_percent,
      "Remaining / Adv": row.amount_remain,
      "Running Balance": row.running_balance
    }));
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Supplier Ledger");
    XLSX.writeFile(workbook, `Ledger_${selectedSup}_${today}.xlsx`);
  };

  const totalBill = ledgerData.reduce((acc, row) => acc + parseFloat(row.total_amount || 0), 0);
  const totalPaid = ledgerData.reduce((acc, row) => {
    const isAdvance = row.pay_type?.toLowerCase().includes('adv');
    return isAdvance ? acc : acc + parseFloat(row.pay_amount || 0);
  }, 0);
  const netPayable = ledgerData.length > 0 ? parseFloat(ledgerData[0].running_balance || 0) : 0;

  return (
      <div className="main-content">
        {/* Header & Filters */}
        <div className="shadow-sm mb-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-indigo-800">Supplier Payment Ledger</h2>
            <div className="flex gap-2">
                <button 
                onClick={handlePrintClick}
                className="bg-indigo-800 text-white px-4 py-2 rounded text-sm font-bold flex items-center gap-2 hover:bg-blue-700 transition-all shadow-sm"
                >
                🖨️ Print View
                </button>
                <button 
                onClick={exportToExcel}
                className="bg-indigo-700 text-white px-4 py-2 rounded text-sm font-bold flex items-center gap-2 hover:bg-green-800 transition-all shadow-sm"
                >
                📊 Export Excel
                </button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex flex-col">
                <label className="text-[10px] font-bold text-gray-400 uppercase mb-1 ml-1">Select Supplier</label>
                <select className="border p-2.5 rounded text-sm outline-none focus:ring-2 focus:ring-blue-400 bg-white" onChange={(e) => setSelectedSup(e.target.value)} value={selectedSup}>
                <option value="">-- Choose Supplier --</option>
                {suppliers.map(sup => <option key={sup.sup_id} value={sup.sup_id}>{sup.sup_name}</option>)}
                </select>
            </div>
            <div className="flex flex-col">
                <label className="text-[10px] font-bold text-gray-400 uppercase mb-1 ml-1">From Date</label>
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="border p-2.5 rounded text-sm outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
            <div className="flex flex-col">
                <label className="text-[10px] font-bold text-gray-400 uppercase mb-1 ml-1">To Date</label>
                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="border p-2.5 rounded text-sm outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-indigo-100 p-4 rounded border-l-4 border-indigo-900 shadow-sm transition-transform hover:scale-[1.02]">
            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Total Bill Amount</p>
            <p className="text-xl font-bold text-gray-800">{totalBill.toLocaleString()}</p>
          </div>
          <div className="bg-indigo-100 p-4 rounded border-l-4 border-indigo-800 shadow-sm transition-transform hover:scale-[1.02]">
            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Total Paid</p>
            <p className="text-xl font-bold text-gray-800">{totalPaid.toLocaleString()}</p>
          </div>
          <div className="bg-indigo-100 p-4 rounded border-l-4 border-indigo-700 shadow-sm transition-transform hover:scale-[1.02]">
            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Net Payable Balance</p>
            <p className={`text-xl font-bold ${netPayable > 0 ? 'text-red-600' : 'text-green-600'}`}>
              {netPayable.toLocaleString()}
            </p>
          </div>
        </div>

        {/* Ledger Table */}
        <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-[#434561] text-white text-[11px] uppercase tracking-wider">
                  <th className="p-4">Date</th>
                  <th className="p-4">Type</th>
                  <th className="p-4 text-right">Total Bill</th>
                  <th className="p-4 text-right">Paid</th>
                  <th className="p-4 text-center">%</th>
                  <th className="p-4 text-right">Running Balance</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {loading ? (
                  <tr><td colSpan="6" className="p-10 text-center text-gray-400 italic">Fetching records...</td></tr>
                ) : ledgerData.length === 0 ? (
                  <tr><td colSpan="6" className="p-10 text-center text-gray-400 italic">No transactions found for this period.</td></tr>
                ) : (
                  ledgerData.map((row, index) => {
                    const isAdvance = row.pay_type?.toLowerCase().includes('adv');
                    return (
                      <tr key={index} className="border-b hover:bg-blue-50/20 transition-colors">
                        <td className="p-4 text-gray-600 font-medium">{new Date(row.created_at).toLocaleDateString('en-GB')}</td>
                        <td className="p-4">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${isAdvance ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-800'}`}>
                            {row.pay_type}
                          </span>
                        </td>
                        <td className="p-4 text-right font-semibold">{parseFloat(row.total_amount || 0).toLocaleString()}</td>
                        <td className="p-4 text-right text-blue-600 font-semibold">{isAdvance ? parseFloat(row.pay_amount) : parseFloat(row.pay_amount || 0).toLocaleString()}</td>
                        <td className="p-4 text-center text-gray-500">{row.pay_percent}%</td>
                        <td className={`p-4 text-right font-black bg-gray-50 ${parseFloat(row.running_balance) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {parseFloat(row.running_balance || 0).toLocaleString()}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

  );
}